import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, FormGroup, Label, Button, Alert } from 'reactstrap';
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents';
import { addData } from '../../lib/sessionschedule';
import { getDataforInstructorActiveStudent } from '../../lib/classdetailview';
import { getCurrentDateInput, getCurrentTimeInput } from '../../utils/dateTimeUtils';
import moment from 'moment';

const ScheduleAddModal = ({ isOpen, toggle, instructorId, studentId, onSave, isDarkMode }) => {
    const [instructorStudent, setInstructorStudent] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState('');
    const [selectedClass, setSelectedClass] = useState('');
    const [period, setPeriod] = useState('OneDate');
    const [startDate, setStartDate] = useState(getCurrentDateInput());
    const [endDate, setEndDate] = useState(getCurrentDateInput(7));
    const [time, setTime] = useState(getCurrentTimeInput());
    const [showEndDate, setShowEndDate] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [showToast, setShowToast] = useState(false);

    const showToastMessage = (message) => {
        setToastMessage(message);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    }

    useEffect(() => {
        if (isOpen) {
            fetchInstructorStudents();
            if (studentId) {
                setSelectedStudent(studentId);
            }
            setStartDate(getCurrentDateInput());
            setEndDate(getCurrentDateInput(7));
            setTime(getCurrentTimeInput());
        }
    }, [isOpen, studentId]);

    const fetchInstructorStudents = async () => {
        try {
            const students = await getDataforInstructorActiveStudent(instructorId);
            if (students && students.length > 0) {
                setInstructorStudent(students);
                if (!selectedStudent) {
                    setSelectedStudent(studentId || students[0].studentId);
                }
            }
        } catch (error) {
            console.error('Error fetching instructor students:', error);
            setError('Failed to load students');
        }
    };

    useEffect(() => {
        if (instructorStudent.length > 0 && selectedStudent) {
            const student = instructorStudent.find((std) => std.studentId === selectedStudent);
            if (student) {
                setSelectedClass(student.classId);
            }
        }
    }, [selectedStudent, instructorStudent]);

    useEffect(() => {
        setShowEndDate(period !== 'OneDate');
    }, [period]);

    const getDateRange = () => {
        let addday = 0;
        let jstr = 'days';

        if (period === 'EveryWeek') addday = 7;
        if (period === 'EveryOtherWeek') addday = 14;
        if (period === 'EveryMonth') {
            addday = 1;
            jstr = 'months';
        }

        const dateArray = [];
        let currentDate = startDate;
        while (currentDate <= endDate) {
            dateArray.push(currentDate);
            currentDate = moment(currentDate).add(addday, jstr).format('YYYY-MM-DD');
        }
        return dateArray;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const recArray = [];

            if (period === 'OneDate') {
                recArray.push({
                    studentId: selectedStudent,
                    classId: selectedClass,
                    instructorId: instructorId,
                    ldate: startDate,
                    ltime: time
                });
            } else {
                const dateArray = getDateRange();
                dateArray.forEach((data) => {
                    recArray.push({
                        studentId: selectedStudent,
                        classId: selectedClass,
                        instructorId: instructorId,
                        ldate: data,
                        ltime: time
                    });
                });
            }

            await addData(recArray);
            onSave(selectedStudent);
            showToastMessage('Schedule added successfully!');
            toggle();
        } catch (error) {
            console.error('Error adding schedule:', error);
            setError('Failed to add schedule');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
            <Modal isOpen={isOpen} toggle={toggle} size="lg" className={isDarkMode ? 'dark-modal' : ''}>
            <ModalHeader toggle={toggle}>Schedule New Meeting</ModalHeader>
            <ModalBody>
                {error && <Alert color="danger">{error}</Alert>}
                <form onSubmit={handleSubmit} id="schedule-add-form">
                    <Row>
                        <Col lg="6">
                            <FormGroup>
                                <Label>Student</Label>
                                <select
                                    value={selectedStudent}
                                    onChange={(e) => setSelectedStudent(e.target.value)}
                                    className="form-control"
                                    required
                                    disabled={!!studentId}
                                >
                                    {instructorStudent.map((data, ix) => (
                                        <option key={ix} value={data.studentId}>
                                            {data.studentName + ' ' + data.studentSurname}
                                        </option>
                                    ))}
                                </select>
                            </FormGroup>
                        </Col>
                        <Col lg="3">
                            <FormGroup>
                                <Label>Date</Label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="form-control"
                                    required
                                />
                            </FormGroup>
                        </Col>
                        <Col lg="3">
                            <FormGroup>
                                <Label>Time</Label>
                                <input
                                    type="time"
                                    value={time}
                                    onChange={(e) => setTime(e.target.value)}
                                    className="form-control"
                                    required
                                />
                            </FormGroup>
                        </Col>
                        <Col lg="6">
                            <FormGroup>
                                <Label>Period</Label>
                                <select
                                    value={period}
                                    onChange={(e) => setPeriod(e.target.value)}
                                    className="form-control"
                                >
                                    <option value="OneDate">One Date</option>
                                    <option value="EveryWeek">Every Week</option>
                                    <option value="EveryOtherWeek">Every Other Week</option>
                                    <option value="EveryMonth">Every Month</option>
                                </select>
                            </FormGroup>
                        </Col>
                        {showEndDate && (
                            <Col lg="6">
                                <FormGroup>
                                    <Label>End Date</Label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="form-control"
                                        required
                                    />
                                </FormGroup>
                            </Col>
                        )}
                    </Row>
                </form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" type="submit" form="schedule-add-form" disabled={loading}>
                    {loading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                            Adding...
                        </>
                    ) : (
                        'Add Schedule'
                    )}
                </Button>
                <Button color="secondary" onClick={toggle} disabled={loading}>
                    Cancel
                </Button>
            </ModalFooter>
            </Modal>
        </>
    );
};

export default ScheduleAddModal;
